import { css } from '@emotion/core';
import { Box, Breadcrumb, Heading, VideoPlayer } from 'lucid-components-react';
import React, { useContext } from 'react';
import ReactHtmlParser from 'react-html-parser';

import Link from 'components/link/link';
import LucidContainer from 'components/lucid-container/lucid-container';
import LayoutContext from 'contexts/layoutContext';
import {
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm
} from 'generated/graphql';
import useMarketoLink from 'hooks/use-marketo-link';
import { mqMax } from 'theme/base';
import { Text } from 'theme/typography';
import { LucidTheme } from 'types/types';

interface Props {
  header: string | string[];
  subheader?: string;
  backgroundImage?: string;
  hasBackgroundImage?: boolean;
  smallBgImage?: boolean;
  breadCrumbHub?: string;
  breadCrumbPage?: string;
  fullSunrise?: boolean;
  pageType?: string;
  videoLink?: string;
  buttons?: Drupal_ParagraphCtaButton[];
}

const SunriseHeader = ({
  header,
  subheader = '',
  buttons,
  backgroundImage = '',
  hasBackgroundImage,
  smallBgImage,
  breadCrumbHub = '',
  breadCrumbPage = '',
  fullSunrise,
  videoLink = '',
  pageType = ''
}: Props) => {
  const { language, video } = useContext(LayoutContext);
  const getMarketoLink = useMarketoLink(language);

  const altText = header && Array.isArray(header) ? header.join('-') : header;

  const spaciousStyles = (theme: LucidTheme) =>
    css({
      padding: `${theme.space[9]}px 0`,
      [mqMax.tabletSmall]: {
        padding: `${theme.space[9]}px 0 ${theme.space[7]}px`
      }
    });

  const mobilePullReset = css({
    [mqMax.tabletSmall]: {
      top: 'auto',
      bottom: 0
    }
  });

  const buttonStyles = (theme: LucidTheme) =>
    css({
      width: '100%',
      margin: `${theme.space[2]}px ${theme.space[0]}`,
      textAlign: 'center'
    });

  const buttonStylesHalfDesktop = (theme: LucidTheme) =>
    css({
      flex: '0 1 48%',
      maxWidth: '48%',
      margin: `${theme.space[2]}px ${theme.space[0]}`,
      '&:first-of-type': {
        marginRight: `${theme.space[2]}px`
      },
      [mqMax.computer]: {
        flex: '0 1 100%',
        maxWidth: '100%',
        '&:first-of-type': {
          marginRight: theme.space[0]
        }
      }
    });

  const buttonsContainer = (theme: LucidTheme) =>
    css({
      maxWidth: '400px',
      '& > .ab-test__branch:not(:empty)': buttonStylesHalfDesktop(theme)
    });

  const mediaVideo = css({
    [mqMax.tabletSmall]: {
      height: 'auto',
      marginBottom: '1px'
    },
    [mqMax.mobile]: {
      display: 'block'
    }
  });

  const containerFullWidthTablet = css({
    [mqMax.computer]: {
      flex: '0 1 100%',
      maxWidth: '100%'
    },
    [mqMax.tabletSmall]: {
      flex: '0 1 100%',
      maxWidth: '100%'
    }
  });

  const containerUnevenSmallDesktop = (theme: LucidTheme) =>
    css({
      flex: '0 1 25%',
      maxWidth: '25%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      [mqMax.tablet]: {
        flex: '0 1 100%',
        maxWidth: '100%'
      },
      p: {
        fontSize: theme.fontSizes[4],
        fontWeight: theme.fontWeights.bold
      }
    });

  return (
    <Box as="section">
      <Box
        css={(theme: LucidTheme) => [
          {
            position: 'relative',
            paddingTop: theme.space[5],
            paddingBottom: theme.space[8],
            [mqMax.mobile]: {
              paddingTop: theme.space[8]
            }
          },
          fullSunrise
            ? spaciousStyles(theme)
            : {
              '&::before': {
                right: '40%',
                [mqMax.computer]: {
                  right: 0
                }
              }
            },
          hasBackgroundImage && !smallBgImage
            ? [
              spaciousStyles(theme),
              {
                paddingBottom: `${theme.space[3]}em`,
                [mqMax.tabletSmall]: {
                  paddingBottom: `${theme.space[8]}px`
                }
              }
            ]
            : {},
          breadCrumbHub && {
            flexDirection: 'column'
          }
        ]}
      >
        <LucidContainer
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap'
          }}
        >
          <Box
            css={(theme: LucidTheme) => [
              fullSunrise
                ? [
                  {
                    paddingBottom: theme.space[8],
                    paddingTop: theme.space[5]
                  }
                ]
                : {
                  flex: '0 1 41%',
                  maxWidth: '41%',
                  [mqMax.tablet]: {
                    flex: '0 1 100%',
                    maxWidth: '100%'
                  }
                },
              pageType === 'individualRole' && containerFullWidthTablet
            ]}
          >
            {breadCrumbHub && (
              <Breadcrumb
                items={[
                  <Link
                    key={breadCrumbHub}
                    secondary
                    to={
                      breadCrumbHub !== 'Solutions'
                        ? `/solutions/${breadCrumbHub.toLowerCase()}`
                        : '/solutions'
                    }
                  >
                    {breadCrumbHub}
                  </Link>,
                  breadCrumbPage
                ]}
              />
            )}
            <Heading
              css={(theme: LucidTheme) => [
                fullSunrise && {
                  paddingRight: theme.space[10]
                },
                {
                  [mqMax.computer]: {
                    padding: theme.space[0]
                  }
                }
              ]}
              maxWidth={{ default: '30rem', tabletSmall: '60rem' }}
              mb={{ default: 0, tabletSmall: 8 }}
              variant="headlineM"
            >
              {header && Array.isArray(header)
                ? ReactHtmlParser(header.join('<br/>'))
                : header}
            </Heading>
            {hasBackgroundImage && (
              <Box
                css={(theme: LucidTheme) => ({
                  flex: '0 1 100%',
                  maxWidth: '100%',
                  paddingRight: theme.space[0],
                  [mqMax.computer]: {
                    flex: '70%',
                    maxWidth: '70%'
                  },
                  [mqMax.tabletSmall]: {
                    flex: '100%',
                    maxWidth: '100%'
                  }
                })}
              >
                {subheader && (
                  <Text
                    css={(theme: LucidTheme) => [
                      { paddingBottom: theme.space[5] }
                    ]}
                    textStyles="callout"
                  >
                    {subheader}
                  </Text>
                )}
                {buttons && (
                  <Box
                    css={(theme: LucidTheme) => [
                      buttonsContainer(theme),
                      !fullSunrise && {
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between'
                      }
                    ]}
                  >
                    {buttons.map((button, i) => {
                      const entity = button.fieldMarketo
                        ? (button.fieldMarketo as {
                          entity: Drupal_ParagraphMarketoForm;
                        })
                        : null;
                      const marketo = entity?.entity || null;
                      const { url, copy } = getMarketoLink(
                        marketo?.fieldMarketoId
                      )!;
                      return (
                        (marketo?.fieldMarketoId && (
                          <Link
                            key={
                              button.fieldButtonText ||
                              `sunrise-header-button-${i}`
                            }
                            asButton
                            css={(theme: LucidTheme) => [
                              buttonsContainer(theme),
                              !fullSunrise && buttonStylesHalfDesktop(theme)
                            ]}
                            primary={i === 0}
                            secondary={i !== 0}
                            to={url}
                          >
                            {button.fieldButtonText || copy}
                          </Link>
                        )) ||
                        (button.fieldButtonLink && (
                          <Link
                            key={
                              button.fieldButtonText ||
                              `sunrise-header-button-${i}`
                            }
                            asButton
                            css={(theme: LucidTheme) => [
                              buttonsContainer(theme),
                              !fullSunrise && buttonStylesHalfDesktop(theme)
                            ]}
                            primary={i === 0}
                            secondary={i !== 0}
                            to={button.fieldButtonLink}
                          >
                            {button.fieldButtonText}
                          </Link>
                        ))
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
          {!hasBackgroundImage && (
            <>
              <Box
                css={(theme: LucidTheme) => [
                  fullSunrise
                    ? {
                      flex: '0 1 100%',
                      maxWidth: '100%'
                    }
                    : containerUnevenSmallDesktop(theme),
                  breadCrumbHub
                    ? [containerFullWidthTablet, mobilePullReset]
                    : {}
                ]}
              >
                <Box
                  css={(theme: LucidTheme) => ({
                    [mqMax.computer]: {
                      flexBasis: '70%',
                      maxWidth: '70%',
                      paddingRight: theme.space[4]
                    },
                    [mqMax.tabletSmall]: {
                      flexBasis: '100%',
                      maxWidth: '100%'
                    }
                  })}
                >
                  {subheader && (
                    <Text
                      css={(theme: LucidTheme) => ({
                        maxWidth: `${theme.space[4]}em`,
                        paddingBottom: theme.space[5]
                      })}
                    >
                      {subheader}
                    </Text>
                  )}

                  {buttons && (
                    <Box
                      css={(theme: LucidTheme) => [
                        {
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'space-between'
                        },
                        buttonsContainer(theme),
                        buttonStyles(theme)
                      ]}
                    >
                      {buttons.map((button, i) => {
                        const entity = button.fieldMarketo
                          ? (button.fieldMarketo as {
                            entity: Drupal_ParagraphMarketoForm;
                          })
                          : null;
                        const marketo = entity?.entity || null;
                        const { url, copy } = getMarketoLink(
                          marketo?.fieldMarketoId
                        )!;
                        return (
                          marketo?.fieldMarketoId && (
                            <Link
                              key={
                                button.fieldButtonText ||
                                `sunrise-header-button-${i}`
                              }
                              asButton
                              css={(theme: LucidTheme) => buttonStyles(theme)}
                              primary={i === 0}
                              secondary={i !== 0}
                              to={url}
                            >
                              {button.fieldButtonText || copy}
                            </Link>
                          )
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
              {!fullSunrise && (
                <Box
                  css={[
                    {
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      width: '100%'
                    },
                    mobilePullReset
                  ]}
                >
                  <Box
                    css={{
                      margin: 0,
                      flexGrow: 0,
                      flexShrink: 1,
                      flexBasis: '100%',
                      maxWidth: '100%'
                    }}
                  />
                  <Box
                    css={{
                      position: 'relative',
                      margin: 0,
                      flexGrow: 0,
                      flexShrink: 1,
                      flexBasis: '8.3%',
                      maxWidth: '8.3%'
                    }}
                  >
                    <Box
                      css={(theme: LucidTheme) => ({
                        '&::before': {
                          content: '""',
                          display: 'block',
                          position: 'absolute',
                          right: 0,
                          bottom: '-10rem',
                          height: '10rem',
                          width: '0.1875rem',
                          borderRight: `0.1875rem solid ${theme.colors.indigo[60]}`
                        },
                        '&::after': {
                          content: '"\u27A4"',
                          display: 'block',
                          position: 'absolute',
                          lineHeight: 0,
                          fontSize: theme.fontSizes[5],
                          right: '2px',
                          height: 0,
                          width: 0,
                          color: theme.colors.indigo[60],
                          transform: 'rotate(90deg)',
                          transformOrigin: 'center center',
                          bottom: '-9.5rem'
                        },
                        [mqMax.computer]: {
                          display: 'none'
                        }
                      })}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </LucidContainer>
        {backgroundImage && (
          <Box
            css={(theme: LucidTheme) => [
              {
                position: 'absolute',
                paddingTop: theme.space[0],
                width: '50%',
                top: theme.space[10],
                bottom: theme.space[5],
                left: '50%',
                marginBottom: theme.space[0],
                [mqMax.tablet]: {
                  position: 'relative',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  height: '500px',
                  width: '100%',
                  paddingTop: theme.space[4],
                  marginBottom: '-3.5rem'
                },
                [mqMax.mobile]: {
                  display: 'none'
                }
              },
              videoLink ? mediaVideo : {}
            ]}
          >
            {videoLink ? (
              <VideoPlayer
                css={mediaVideo}
                onClick={() => video.getVideoModal(videoLink)}
                placeholder={() => <img alt={altText} src={backgroundImage} />}
              />
            ) : (
                /* eslint-disable-next-line react/jsx-indent */
                <img
                  alt={altText}
                  css={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover'
                  }}
                  src={backgroundImage}
                />
              )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SunriseHeader;
